// Single client-side contract file for the notification_center API.
// Every shape here mirrors the Go service responses 1:1 — when the backend
// contract moves, this file is the only place to update.

export type Channel = 'in_app' | 'email' | 'push' | 'sms' | 'webhook' | 'mock'
export type DeliveryStatus = 'queued' | 'processing' | 'sent' | 'failed' | 'skipped'
export type ChannelType = 'email' | 'sms' | 'fcm' | 'webhook' | 'mock'
export type ClientType = 'service' | 'web' | 'mobile' | 'portal'

export type TriggerRecipient = { subscriberId: string } | { type: 'topic'; topicKey: string }

export type TriggerRequest = {
  name: string
  to: TriggerRecipient[]
  payload: Record<string, unknown>
  overrides?: { email?: { attachments?: { name: string; url: string }[] } }
  actor?: unknown
  transactionId?: string
}

export type TriggerResponse = {
  transactionId: string
  eventId: string
  acknowledged: boolean
  status: 'processed' | 'discarded' | 'duplicate'
  deliveries: number
}

export type Delivery = {
  id: number
  eventId: string
  workflowKey: string
  subscriberId: string
  channel: Channel
  priority: number
  status: DeliveryStatus
  attempts: number
  nextRetryAt: string
  sentAt?: string
  lastError?: string
  renderedSubject?: string
  renderedBody?: string
  createdAt: string
  updatedAt: string
  destinationKind?: 'client' | 'channel'
  destinationRef?: string
  source?: string
}

export type NotiEvent = {
  eventId: string
  transactionId: string
  workflowKey: string
  source: string
  status: 'processed' | 'discarded'
  recipients: unknown
  payload: Record<string, unknown>
  overrides?: unknown
  createdAt: string
}

// One row of the per-transaction audit trail (POC TOR ข้อ 9.2). Mirrors the
// engine's transaction_audit table — all 13 TOR fields incl. device.
export type TxnAudit = {
  id: number
  timestamp: string
  eventId?: string
  transactionId?: string
  userId?: string
  employeeId?: string
  role?: string
  action: string
  module: string
  targetObject?: string
  beforeValue?: unknown
  afterValue?: unknown
  status: string
  approvedBy?: string
  ipAddress?: string
  device?: string
}

export type EventDetail = { event: NotiEvent; deliveries: Delivery[]; audit?: TxnAudit[] }

export type WorkflowStep = { channel: Channel; enabled: boolean; updatedAt: string }

export type ParamKV = { key: string; value: string }

export type Destination = {
  id: number
  position: number
  kind: 'client' | 'channel'
  ref: string
  enabled: boolean
  paramMap: ParamKV[]
  legacyChannel?: string
  updatedAt: string
}

export type DestinationInput = {
  kind: 'client' | 'channel'
  ref: string
  enabled?: boolean
  paramMap?: ParamKV[]
  legacyChannel?: string
}

export type PayloadSchema = Record<string, { description?: string; example?: string; required?: boolean; default?: string }>

// Per-workflow default values for the transaction audit (TOR ข้อ 9.2). Applied
// when the caller doesn't pass the field in the trigger `actor`.
export type AuditDefaults = {
  role?: string
  employeeId?: string
  action?: string
  targetObject?: string
  beforeValue?: string
  approvedBy?: string
}

export type Workflow = {
  key: string
  name: string
  description: string
  critical: boolean
  active: boolean
  priority: number
  createdAt: string
  updatedAt: string
  steps?: WorkflowStep[]
  payloadSchema?: PayloadSchema
  allowedClients: string[]
  destinations?: Destination[]
  auditDefaults?: AuditDefaults
}

export type NotiClient = {
  id: string
  name: string
  type: ClientType
  description: string
  enabled: boolean
  isAdmin: boolean
  hasSecret: boolean
  secretHint?: string
  secretRotatedAt?: string
  createdAt: string
  updatedAt: string
}

export type NotiChannel = {
  id: string
  name: string
  type: ChannelType
  enabled: boolean
  config: Record<string, unknown>
  configured: boolean
  lastTestAt?: string
  lastTestStatus?: '' | 'ok' | 'failed'
  lastTestDetail?: string
  createdAt: string
  updatedAt: string
}

export type ChannelTestResult = { id: string; status: 'ok' | 'failed'; detail: string; testedAt: string }

export type EventSummary = {
  eventId: string
  transactionId: string
  workflowKey: string
  source: string
  status: 'processed' | 'discarded'
  recipientCount: number
  statusCounts: Partial<Record<DeliveryStatus, number>>
  createdAt: string
}

export type EventsPage = { data: EventSummary[]; page: number; limit: number; total: number }

export type EventFilters = {
  source?: string
  workflow?: string
  status?: string
  transactionId?: string
  from?: string
  to?: string
  page?: number
  limit?: number
}

export type KeyCount = { key: string; count: number }

export type StatsOverview = {
  since: string
  events: number
  eventsBySource: KeyCount[]
  statusCounts: Partial<Record<DeliveryStatus, number>>
  successRate: number | null
  latency: { avgMs: number | null; p50Ms: number | null; p95Ms: number | null }
  queueDepth: number
  topWorkflows: KeyCount[]
  recentFailures: Delivery[]
}

export type TimeseriesRow = { bucket: string; group: string; count: number }
export type Timeseries = { since: string; bucket: string; groupBy: string; rows: TimeseriesRow[] }

export type ClientAttributes = { clientId: string; attributes: Record<string, string> }

export type TemplateDetail = {
  workflowKey: string
  channel: Channel
  locale: string
  subject: string
  body: string
  updatedAt: string
}

export type FeedItem = {
  id: number
  workflowKey: string
  title: string
  body: string
  action?: { deepLink?: string; label?: string } & Record<string, unknown>
  readAt?: string
  createdAt: string
}

export type FeedResponse = {
  data: FeedItem[]
  page: number
  limit: number
  total: number
  unreadCount: number
}

export type DeliveriesPage = { data: Delivery[]; page: number; limit: number; total: number }

export type DeliveryFilters = {
  workflow?: string
  channel?: string
  status?: string
  subscriber?: string
  source?: string
  destinationKind?: string
  destinationRef?: string
  page?: number
  limit?: number
}

export type StatRow = { workflowKey: string; channel: Channel; status: DeliveryStatus; count: number }
export type AdminStats = { since: string; events: number; deliveries: StatRow[] }

export type PreferenceWorkflow = {
  workflowKey: string
  name: string
  critical: boolean
  channels: Partial<Record<Channel, boolean>>
}

export type DocPage = { slug: string; title: string }
export type DocContent = { slug: string; title: string; markdown: string }

export type ApiError = { error: { code: string; message: string } }

const BASE = '/api/portal/noti'

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'content-type': 'application/json', accept: 'application/json' },
    ...init,
  })
  const body = await res.json().catch(() => ({ error: { code: 'bad_json', message: `HTTP ${res.status}` } }))
  if (!res.ok) {
    const msg = (body as ApiError)?.error?.message ?? `HTTP ${res.status}`
    throw new Error(msg)
  }
  return body as T
}

export const notiApi = {
  trigger: (body: TriggerRequest) =>
    req<TriggerResponse>('/events/trigger', { method: 'POST', body: JSON.stringify(body) }),

  getEvent: (eventId: string) => req<EventDetail>(`/events/${eventId}`),

  listWorkflows: () => req<{ data: Workflow[] }>('/admin/workflows'),

  patchWorkflow: (key: string, patch: { active?: boolean; critical?: boolean }) =>
    req<Workflow>(`/admin/workflows/${key}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  patchStep: (key: string, channel: Channel, enabled: boolean) =>
    req<WorkflowStep>(`/admin/workflows/${key}/steps/${channel}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),

  getTemplate: (key: string, channel: Channel, locale = 'th') =>
    req<TemplateDetail>(`/admin/workflows/${key}/templates/${channel}?locale=${locale}`),

  putTemplate: (key: string, channel: Channel, body: { subject: string; body: string; locale?: string }) =>
    req<TemplateDetail>(`/admin/workflows/${key}/templates/${channel}`, {
      method: 'PUT',
      body: JSON.stringify({ locale: 'th', ...body }),
    }),

  listDeliveries: (f: DeliveryFilters) => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') q.set(k, String(v))
    return req<DeliveriesPage>(`/admin/deliveries?${q}`)
  },

  stats: (since = '24h') => req<AdminStats>(`/admin/stats?since=${since}`),

  feed: (subscriberId: string, page = 1, limit = 10) =>
    req<FeedResponse>(`/subscribers/${subscriberId}/notifications?page=${page}&limit=${limit}`),

  markRead: (subscriberId: string, notificationId: number) =>
    req<{ readAt: string }>(`/subscribers/${subscriberId}/notifications/${notificationId}/read`, { method: 'POST' }),

  markAllRead: (subscriberId: string) =>
    req<{ updated: number }>(`/subscribers/${subscriberId}/notifications/read-all`, { method: 'POST' }),

  preferences: (subscriberId: string) =>
    req<{ data: PreferenceWorkflow[] }>(`/subscribers/${subscriberId}/preferences`),

  putPreference: (subscriberId: string, workflowKey: string, channel: Channel, enabled: boolean) =>
    req<{ enabled: boolean }>(`/subscribers/${subscriberId}/preferences/${workflowKey}`, {
      method: 'PUT',
      body: JSON.stringify({ channel, enabled }),
    }),

  docsIndex: () => req<{ data: DocPage[] }>('/docs'),

  docContent: (slug: string) => req<DocContent>(`/docs/${slug}`),

  // ── Clients ────────────────────────────────────────────────────────────────
  listClients: () => req<{ data: NotiClient[] }>('/admin/clients'),

  getClient: (id: string) => req<NotiClient>(`/admin/clients/${id}`),

  createClient: (body: { id: string; name: string; type?: ClientType; description?: string }) =>
    req<{ client: NotiClient; secret: string }>('/admin/clients', { method: 'POST', body: JSON.stringify(body) }),

  patchClient: (id: string, patch: { name?: string; type?: ClientType; description?: string; enabled?: boolean }) =>
    req<NotiClient>(`/admin/clients/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  deleteClient: (id: string) => req<{ deleted: string }>(`/admin/clients/${id}`, { method: 'DELETE' }),

  rotateClientSecret: (id: string) =>
    req<{ client: NotiClient; secret: string }>(`/admin/clients/${id}/secret`, { method: 'POST' }),

  clientAttributes: (id: string) => req<ClientAttributes>(`/clients/${id}/attributes`),

  putClientAttributes: (id: string, attributes: Record<string, string>) =>
    req<ClientAttributes>(`/admin/clients/${id}/attributes`, { method: 'PUT', body: JSON.stringify({ attributes }) }),

  // ── Channels ───────────────────────────────────────────────────────────────
  listChannels: () => req<{ data: NotiChannel[] }>('/admin/channels'),

  getChannel: (id: string) => req<NotiChannel>(`/admin/channels/${id}`),

  createChannel: (body: { id: string; name: string; type: ChannelType; config?: Record<string, unknown> }) =>
    req<NotiChannel>('/admin/channels', { method: 'POST', body: JSON.stringify(body) }),

  patchChannel: (id: string, patch: { name?: string; enabled?: boolean; config?: Record<string, unknown> }) =>
    req<NotiChannel>(`/admin/channels/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  deleteChannel: (id: string) => req<{ deleted: string }>(`/admin/channels/${id}`, { method: 'DELETE' }),

  testChannel: (id: string, to?: string) =>
    req<ChannelTestResult>(`/admin/channels/${id}/test`, {
      method: 'POST',
      ...(to ? { body: JSON.stringify({ to }) } : {}),
    }),

  // ── Workflows v2 ───────────────────────────────────────────────────────────
  getWorkflow: (key: string) => req<Workflow>(`/admin/workflows/${key}`),

  createWorkflow: (body: {
    key: string; name: string; description?: string; critical?: boolean; priority?: number
    payloadSchema?: PayloadSchema; allowedClients?: string[]; destinations?: DestinationInput[]
  }) => req<Workflow>('/admin/workflows', { method: 'POST', body: JSON.stringify(body) }),

  patchWorkflowV2: (key: string, patch: {
    active?: boolean; critical?: boolean; name?: string; description?: string
    payloadSchema?: PayloadSchema; allowedClients?: string[]; auditDefaults?: AuditDefaults
  }) => req<Workflow>(`/admin/workflows/${key}`, { method: 'PATCH', body: JSON.stringify(patch) }),

  deleteWorkflow: (key: string) => req<{ deleted: string }>(`/admin/workflows/${key}`, { method: 'DELETE' }),

  listDestinations: (key: string) => req<{ data: Destination[] }>(`/admin/workflows/${key}/destinations`),

  putDestinations: (key: string, destinations: DestinationInput[]) =>
    req<{ data: Destination[] }>(`/admin/workflows/${key}/destinations`, {
      method: 'PUT',
      body: JSON.stringify({ destinations }),
    }),

  patchDestination: (key: string, destId: number, enabled: boolean) =>
    req<Destination>(`/admin/workflows/${key}/destinations/${destId}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }),

  // ── Transactions ───────────────────────────────────────────────────────────
  listEvents: (f: EventFilters) => {
    const q = new URLSearchParams()
    for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== '') q.set(k, String(v))
    return req<EventsPage>(`/admin/events?${q}`)
  },

  deleteEvent: (eventId: string) =>
    req<{ deleted: string; deliveries: number; notifications: number }>(`/admin/events/${eventId}`, { method: 'DELETE' }),

  resendEvent: (eventId: string) =>
    req<TriggerResponse>(`/admin/events/${eventId}/resend`, { method: 'POST' }),

  // ── Dashboard stats ────────────────────────────────────────────────────────
  statsOverview: (since = '24h') => req<StatsOverview>(`/admin/stats/overview?since=${since}`),

  statsTimeseries: (since = '24h', bucket = '1h', groupBy = 'status') =>
    req<Timeseries>(`/admin/stats/timeseries?since=${since}&bucket=${bucket}&groupBy=${groupBy}`),
}

export const streamUrl = {
  subscriber: (subscriberId: string) => `${BASE}/subscribers/${subscriberId}/stream`,
  admin: () => `${BASE}/admin/stream`,
}

export const TERMINAL_STATUSES: DeliveryStatus[] = ['sent', 'failed', 'skipped']

export function allTerminal(deliveries: Delivery[] | undefined): boolean {
  if (!deliveries || deliveries.length === 0) return false
  return deliveries.every((d) => TERMINAL_STATUSES.includes(d.status))
}
